import React, { useEffect, useState, useCallback, memo } from "react";
import { Search, X, ChevronDown, Filter, Check } from "lucide-react";

const styles = {
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.75rem',
    width: '100%',
  },
  searchWrapper: {
    position: 'relative',
    flex: 1,
    minWidth: '200px',
    maxWidth: '24rem',
  },
  searchIcon: {
    position: 'absolute',
    left: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '1rem',
    height: '1rem',
    color: '#94a3b8',
  },
  searchInput: {
    width: '100%',
    paddingLeft: '2.5rem',
    paddingRight: '2.5rem',
    paddingTop: '0.625rem',
    paddingBottom: '0.625rem',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    outline: 'none',
    transition: 'all 0.15s ease',
  },
  clearButton: {
    position: 'absolute',
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    padding: '0.25rem',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  },
  resultsCounter: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#f1f5f9',
    borderRadius: '0.5rem',
  },
  filterWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.5rem',
  },
  filterLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '0.875rem',
    color: '#64748b',
    marginRight: '0.25rem',
  },
  filterBadge: {
    marginLeft: '0.25rem',
    padding: '0.125rem 0.375rem',
    fontSize: '0.75rem',
    fontWeight: 500,
    backgroundColor: '#1a1a1a',
    color: 'white',
    borderRadius: '9999px',
  },
  filterButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.4rem 0.65rem',
    fontSize: '0.8125rem',
    fontWeight: 500,
    borderRadius: '0.5rem',
    border: '1px solid #e2e8f0',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    backgroundColor: 'white',
    color: '#334155',
  },
  filterButtonActive: {
    backgroundColor: '#1a1a1a',
    color: 'white',
    border: '1px solid #1a1a1a',
  },
  filterButtonHover: {
    border: '1px solid #DEC2AA',
    backgroundColor: '#faf6f1',
  },
  filterCountBadge: {
    padding: '0.125rem 0.375rem',
    fontSize: '0.75rem',
    fontWeight: 500,
    backgroundColor: 'white',
    color: '#1a1a1a',
    borderRadius: '9999px',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: '0.25rem',
    minWidth: '200px',
    maxHeight: '300px',
    overflowY: 'auto',
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    zIndex: 50,
  },
  dropdownOption: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    textAlign: 'left',
    borderRadius: '0.375rem',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  },
  checkbox: {
    width: '1rem',
    height: '1rem',
    borderRadius: '0.25rem',
    border: '1px solid #cbd5e1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
  },
  checkboxChecked: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #1a1a1a',
  },
  clearAllButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    color: '#dc2626',
    borderRadius: '0.5rem',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
};

const FilterButton = memo(({ title, activeCount, isOpen, onClick, replacetxt }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const getStyle = () => {
    if (activeCount > 0) {
      return { ...styles.filterButton, ...styles.filterButtonActive };
    }
    if (isHovered) {
      return { ...styles.filterButton, ...styles.filterButtonHover };
    }
    return styles.filterButton;
  };
  
  return (
    <button
      onClick={onClick}
      style={getStyle()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span>{replacetxt?.[title] || title}</span>
      {activeCount > 0 && (
        <span style={styles.filterCountBadge}>
          {activeCount}
        </span>
      )}
      <ChevronDown style={{ 
        width: '1rem', 
        height: '1rem', 
        transition: 'transform 0.15s ease',
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      }} />
    </button>
  );
});

const DropdownOption = memo(({ option, isSelected, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <button
      onClick={onClick}
      style={{
        ...styles.dropdownOption,
        backgroundColor: isHovered ? '#f8fafc' : 'transparent',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{option}</span>
      <div style={{
        ...styles.checkbox,
        ...(isSelected ? styles.checkboxChecked : {}),
      }}>
        {isSelected && (
          <Check style={{ width: '0.75rem', height: '0.75rem', color: 'white' }} />
        )}
      </div>
    </button>
  );
});

const ClearButton = memo(({ onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <button
      onClick={onClick}
      style={{
        ...styles.clearAllButton,
        backgroundColor: isHovered ? '#fef2f2' : 'transparent',
        color: isHovered ? '#b91c1c' : '#dc2626',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <X style={{ width: '1rem', height: '1rem' }} />
      <span>Clear all</span>
    </button>
  );
});

const SearchAndFilter = memo(({
  replacetxt,
  count,
  search,
  setsearch,
  showing_filters,
  current_filters,
  setcurrent_filters,
  filter_modal,
  clear_filters,
}) => {
  const [dropdown_open, setdropdown_open] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [clearHovered, setClearHovered] = useState(false);

  useEffect(() => {
    const handleClick = () => setdropdown_open("");
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    const disallowedCharacters = /[()?+*\\]/;
    setsearch(value?.replace(disallowedCharacters, "").toLowerCase());
  }, [setsearch]);

  const hasActiveFilters = Object.keys(current_filters || {}).some(
    (el) => current_filters[el]?.length > 0
  );

  const totalActiveFilters = Object.values(current_filters || {}).reduce(
    (acc, arr) => acc + (arr?.length || 0), 0
  );

  return (
    <div style={styles.container}>
      <div style={styles.searchWrapper}>
        <Search style={styles.searchIcon} />
        <input
          type="text"
          placeholder="Search investors, firms, locations..."
          value={search}
          onChange={handleSearchChange}
          style={{
            ...styles.searchInput,
            boxShadow: searchFocused ? '0 0 0 2px #1a1a1a' : 'none',
            border: searchFocused ? '1px solid transparent' : '1px solid #e2e8f0',
          }}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
        />
        {search.length > 0 && (
          <button
            onClick={() => setsearch("")}
            style={{
              ...styles.clearButton,
              backgroundColor: clearHovered ? '#e2e8f0' : 'transparent',
            }}
            onMouseEnter={() => setClearHovered(true)}
            onMouseLeave={() => setClearHovered(false)}
          >
            <X style={{ width: '1rem', height: '1rem', color: '#64748b' }} />
          </button>
        )}
      </div>

      {count !== undefined && (
        <div style={styles.resultsCounter}>
          <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Results:</span>
          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1a1a1a' }}>{count}</span>
        </div>
      )}

      {filter_modal && showing_filters?.length > 0 && (
        <div style={styles.filterWrapper}>
          <div style={styles.filterLabel}>
            <Filter style={{ width: '1rem', height: '1rem' }} />
            <span>Filters</span>
            {totalActiveFilters > 0 && (
              <span style={styles.filterBadge}>
                {totalActiveFilters}
              </span>
            )}
          </div>

          {showing_filters.map(({ title, children }) => {
            const activeCount = current_filters?.[title]?.length || 0;
            const isOpen = dropdown_open === title;
            
            return (
              <div key={title} style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                <FilterButton
                  title={title}
                  activeCount={activeCount}
                  isOpen={isOpen}
                  onClick={() => setdropdown_open(prev => prev === title ? "" : title)}
                  replacetxt={replacetxt}
                />

                {isOpen && (
                  <div style={styles.dropdown}>
                    <div style={{ padding: '0.5rem' }}>
                      {children.map((option) => option && (
                        <DropdownOption
                          key={option}
                          option={option}
                          isSelected={current_filters?.[title]?.includes(option)}
                          onClick={() => setcurrent_filters(title, option)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {hasActiveFilters && (
            <ClearButton onClick={clear_filters} />
          )}
        </div>
      )}
    </div>
  );
});

export default SearchAndFilter;
